import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { SquarePen } from "lucide-react";

import { isSuperAdmin } from "../../../../utils/Permissions";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import messages from "../../../../assets/locale/messages";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import Filters from "../../../../components/Filter";
import { UIEmptyState, UIPaginationBar } from "../../../../components/ui";
import { getProfileEditRequestsRequest } from "../../../../store/ProfileEditRequests/actions";
import { ITEMS_PER_PAGE } from "../../../../network/apis/companies";
import ProfileEditRequestsTable from "./components/ProfileEditRequestsTable";

import "./ProfileEditRequestsPage.scss";

// This is a "Requests" screen — once a request has been decided (approved/
// rejected) it's no longer a pending request, so only the still-actionable
// statuses are ever fetched or offered as filter options.
const STATUS_OPTION_VALUES = ["pending", "in_review"];

const ProfileEditRequestsPage = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.profileEditRequests.list);
  const meta = useSelector((state) => state.profileEditRequests.meta);
  const { shared, profileEditRequests: per } = messages[lang];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Single-select, not multi — with only 2 possible statuses, "select both"
  // and "select none" already mean the same thing (show everything).
  const [statusFilter, setStatusFilter] = useState(null);
  const prevSearchInput = useRef(null);

  const statusOptions = STATUS_OPTION_VALUES.map((value, index) => ({
    id: index + 1,
    name: per.statusOptions[value],
    value,
  }));

  useEffect(() => {
    const t = setTimeout(() => {
      if (
        prevSearchInput.current !== null &&
        prevSearchInput.current !== searchInput
      ) {
        setPage(1);
      }
      prevSearchInput.current = searchInput;
      setDebouncedSearch(searchInput);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchList = () => {
    dispatch(
      getProfileEditRequestsRequest({
        page,
        items: pageSize,
        status: statusFilter ? [statusFilter.value] : [],
        company_name: debouncedSearch?.trim() || undefined,
      }),
    );
  };

  useEffect(fetchList, [
    dispatch,
    page,
    pageSize,
    debouncedSearch,
    statusFilter,
    lang,
  ]);

  if (!isSuperAdmin()) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const total = Number(meta.count) || 0;
  const currentPage = Number(meta.currentPage) || page;
  const from = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, total);

  const renderBody = () => {
    if (!isLoading && (!list || list.length === 0)) {
      return (
        <div className="profile-edit-requests-page__empty">
          <UIEmptyState
            title={per.emptyTitle}
            description={per.emptyDescription}
            icon={<SquarePen aria-hidden />}
            classNames={{ root: "ui-empty-state--embedded" }}
          />
        </div>
      );
    }

    if (!list || list.length === 0) {
      return null;
    }

    return (
      <>
        <ProfileEditRequestsTable rows={list} messagesBundle={per} />
        <UIPaginationBar
          currentPage={currentPage}
          pages={meta.pages || 1}
          pageSize={pageSize}
          rangeTemplate={per.showingRange}
          rangeFrom={from}
          rangeTo={to}
          rangeTotal={total}
          rowsPerPageLabel={shared.numberOfRows}
          onPageChange={(nextPage) => setPage(nextPage)}
          onPageSizeChange={(nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          }}
        />
      </>
    );
  };

  return (
    <div className="profile-edit-requests-page">
      <div className="mt-3">
        <Breadcrumbs
          list={[{ path: "", label: per.listBreadcrumb }]}
          BreadCrumbHasBackIcon={false}
        />
      </div>
      <Filters
        isCollapsable
        searchInput={{
          placeholder: per.searchPlaceholder,
          name: "profileEditRequestsSearch",
          value: searchInput,
          onChange: (value) => setSearchInput(value),
        }}
        filters={[
          {
            type: "filter",
            name: "profileEditRequestsStatus",
            placeholder: per.statusFilterLabel,
            label: per.statusFilterLabel,
            value: statusFilter,
            options: statusOptions,
            onChange: (value) => {
              setStatusFilter(value);
              setPage(1);
            },
          },
        ]}
      />
      <div className="d-flex flex-column justify-content-between">
        {renderBody()}
      </div>
    </div>
  );
};

export default ProfileEditRequestsPage;
